/* andjuly waitlist: background word, products in turn, image fade-in, owner password dialog, sign-up states. */
(() => {
  'use strict';

  /* ---------- Background word ----------
     "type" (default): the word sits large behind the product and a caret backspaces it and types
     it again, like a text field. "loop": a pen writes the word along a slow loop around the
     product, each letter turned to the direction of travel and fading behind it. "still", and
     every visitor who asks for reduced motion: the whole word, nothing moves. Nothing follows
     the pointer. Both animations pause while the hero is off screen or the tab is hidden. */
  const FONT = '500 12px Inter, "Inter fallback", Arial, sans-serif';
  const INK = '19,19,23';
  const LIFE = 3200;          // ms a looped letter stays
  const PEAK = 0.6;           // alpha of a fresh looped letter
  const TRACK = 1.5;          // px between looped letters
  const GAP = 14;             // px between looped words
  const MAX = 1400;           // looped letters kept at once
  const DWELL = 6000;          // ms per product when the word is not typed
  let bg = null, bgToken = null, pieces = null;

  function watch(host, off, show, hide) {
    let onScreen = true;
    const update = () => (onScreen && document.visibilityState === 'visible' ? show() : hide());
    document.addEventListener('visibilitychange', update, { signal: off.signal });
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; update(); });
      io.observe(host);
      off.signal.addEventListener('abort', () => io.disconnect());
    }
    update();
  }

  function startType(hero, word, onRest) {
    const line = hero.querySelector('.aj-type__line');
    if (!line) return null;
    line.textContent = '';
    const letters = Array.from(word, (ch) => {
      const s = document.createElement('span');
      s.textContent = ch;
      line.append(s);
      return s;
    });
    const caret = document.createElement('span');
    caret.className = 'aj-type__caret';
    line.append(caret);
    const L = letters.length;
    const beat = word.indexOf('studios');  // a short pause between "andjuly" and "studios"
    const off = new AbortController();
    let n = L, phase = 'hold', timer = 0, running = false;

    function show(k) {
      letters.forEach((s, i) => s.classList.toggle('is-off', i >= k));
      if (k) letters[k - 1].after(caret); else line.prepend(caret);
    }
    // The page opens on the whole word; the caret blinks, backspaces it, rests, types it again.
    function next() {
      let wait;
      if (phase === 'hold') { phase = 'erase'; wait = 80; }
      else if (phase === 'erase') {
        show(--n);
        if (n === 0) { phase = 'rest'; wait = 1100; if (onRest) onRest(); } else wait = 36;
      } else if (phase === 'rest') { phase = 'type'; wait = 0; }
      else {
        show(++n);
        if (n === L) { phase = 'hold'; wait = 3600; }
        else wait = 75 + Math.random() * 95 + (n === beat ? 300 : 0);
      }
      line.classList.toggle('is-typing', phase === 'erase' || phase === 'type');
      timer = setTimeout(next, wait);
    }
    watch(hero, off,
      () => { if (!running) { running = true; timer = setTimeout(next, phase === 'hold' ? 2600 : 400); } },
      () => { running = false; clearTimeout(timer); });

    return {
      stop() {
        off.abort(); clearTimeout(timer);
        show(L); caret.remove(); line.classList.remove('is-typing');
      }
    };
  }

  function startLoop(hero, canvas, word) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const off = new AbortController();
    let w = 0, h = 0, dpr = 1, raf = 0, running = false;
    let loop = { cx: 0, cy: 0, rx: 0, ry: 0 };  // around the product
    const marks = [];           // { x, y, a (angle), ch, t (birth) }
    const adv = {};
    let seq = 0;                // position in the word, carried across the loop

    function size() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = hero.clientWidth; h = hero.clientHeight;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.font = FONT; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      for (const c of word) adv[c] = ctx.measureText(c).width + TRACK;
      const hr = hero.getBoundingClientRect();
      const piece = hero.querySelector('.aj-piece');
      const pr = piece ? piece.getBoundingClientRect() : hr;
      loop = {
        cx: pr.left - hr.left + pr.width / 2, cy: pr.top - hr.top + pr.height / 2,
        rx: Math.min(w / 2 - 8, pr.width * 0.72), ry: Math.min(h / 2 - 8, pr.height * 0.6)
      };
    }

    const turn = (from, to, k) => {
      if (from === null) return to;
      let d = to - from;
      while (d > Math.PI) d -= 2 * Math.PI;
      while (d < -Math.PI) d += 2 * Math.PI;
      return from + d * k;
    };

    // A stroke is { dist, next, left }: how far it has walked, where the next letter's centre
    // is due, and whether it is heading left. Letters sit their own width apart and a gap
    // follows each word. Heading left, the word is laid down from its end and the letters are
    // turned upright, so it still reads left to right.
    const L = word.length;
    const charAt = (i, left) => word[left ? L - 1 - (i % L) : i % L];
    const stroke = () => ({ dist: 0, next: adv[word[seq % L]] / 2, left: false });
    function walk(x0, y0, x1, y1, a, st, now) {
      const dx = x1 - x0, dy = y1 - y0, len = Math.hypot(dx, dy);
      if (len < 0.5) return;
      const c = Math.cos(a);
      if (st.left ? c > 0.25 : c < -0.25) {  // turned around: start a fresh word after a gap
        st.left = !st.left;
        if (seq % L) { seq += L - (seq % L); st.next += GAP; }
      }
      const up = st.left ? a + Math.PI : a;
      while (st.next <= st.dist + len) {
        const k = (st.next - st.dist) / len;
        const ch = charAt(seq, st.left);
        marks.push({ x: x0 + dx * k, y: y0 + dy * k, a: up, ch, t: now });
        seq++;
        st.next += adv[ch] / 2 + adv[charAt(seq, st.left)] / 2 + (seq % L === 0 ? GAP : 0);
      }
      st.dist += len;
      if (marks.length > MAX) marks.splice(0, marks.length - MAX);
    }

    let pen = null, ang = null, st = null, phase = 0, last = null;
    function step(t) {
      // Advance by pixels, not time: a phone's smaller loop would otherwise crawl.
      const dt = last === null ? 0 : Math.min(64, t - last);
      last = t;
      phase += dt * 0.00016 * Math.max(1, 480 / ((loop.rx + loop.ry) / 2 || 480));
      const x = loop.cx + loop.rx * (0.9 * Math.sin(phase * 1.9 + 0.7) + 0.1 * Math.sin(phase * 5.3));
      const y = loop.cy + loop.ry * (0.86 * Math.sin(phase * 1.27) + 0.14 * Math.cos(phase * 3.7));
      if (pen) {
        ang = turn(ang, Math.atan2(y - pen.y, x - pen.x), 0.25);
        walk(pen.x, pen.y, x, y, ang, st, t);
      } else {
        st = stroke(); ang = null;
      }
      pen = { x, y };
    }

    function frame(t) {
      raf = 0;
      step(t);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = FONT; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      let n = 0;
      for (let i = 0; i < marks.length; i++) {
        const m = marks[i];
        const age = (t - m.t) / LIFE;
        if (age >= 1) continue;
        marks[n++] = m;
        const alpha = PEAK * Math.min(1, (t - m.t) / 90) * Math.pow(1 - age, 1.4);
        const c = Math.cos(m.a) * dpr, sn = Math.sin(m.a) * dpr;
        ctx.setTransform(c, sn, -sn, c, m.x * dpr, m.y * dpr);
        ctx.fillStyle = `rgba(${INK},${alpha.toFixed(3)})`;
        ctx.fillText(m.ch, 0, 0);
      }
      marks.length = n;
      if (running) raf = requestAnimationFrame(frame);
    }

    let lastW = 0, rt = 0;
    window.addEventListener('resize', () => {
      clearTimeout(rt);
      rt = setTimeout(() => {
        if (hero.clientWidth === lastW && Math.abs(hero.clientHeight - h) < 80) return;
        lastW = hero.clientWidth;
        size();
      }, 120);
    }, { signal: off.signal });

    size(); lastW = w;
    watch(hero, off,
      () => { if (!running) { running = true; last = null; raf = requestAnimationFrame(frame); } },
      () => { running = false; if (raf) { cancelAnimationFrame(raf); raf = 0; } });

    return {
      stop() {
        off.abort(); clearTimeout(rt); running = false;
        if (raf) cancelAnimationFrame(raf);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }

  /* ---------- Products: one at a time ----------
     Every "Product" block sits in the same box and one shows. With the typed word, the next
     product fades in while the word is erased, so each retype lands on a new piece; with the
     loop or still word it moves on every DWELL ms. The lines under the caption show a product
     and stop the turns. Reduced motion: no turns, the lines still work. Paused off screen. */
  function startPieces(hero, mode) {
    const fig = hero.querySelector('[data-aj-pieces]');
    const slots = fig ? Array.from(fig.querySelectorAll('.aj-piece__slot')) : [];
    if (slots.length < 2) return null;
    const caps = Array.from(fig.querySelectorAll('.aj-piece__cap > span'));
    const ticks = Array.from(fig.querySelectorAll('.aj-piece__tick'));
    const bar = fig.querySelector('.aj-piece__ticks');
    const off = new AbortController();
    let at = Math.max(0, slots.findIndex((s) => s.classList.contains('is-active')));
    let held = mode === 'off', busy = false, timer = 0, running = false;

    const flag = (els, k) => els.forEach((el, j) => {
      el.classList.toggle('is-active', j === k);
      if (j === k) el.removeAttribute('aria-hidden'); else el.setAttribute('aria-hidden', 'true');
    });
    function show(k) {
      at = k; flag(slots, k); flag(caps, k);
      ticks.forEach((t, j) => t.setAttribute('aria-current', String(j === k)));
    }
    // The next product that has loaded: a failed photo is skipped, one still loading is waited for.
    function advance() {
      if (held || busy) return;
      for (let step = 1; step < slots.length; step++) {
        const k = (at + step) % slots.length;
        if (slots[k].classList.contains('is-missing')) continue;
        const img = slots[k].querySelector('img');
        if (!img || !img.complete || !img.naturalWidth) return;
        busy = true;
        const go = () => { busy = false; if (!held && !off.signal.aborted) show(k); };
        (img.decode ? img.decode() : Promise.resolve()).then(go, go);
        return;
      }
    }
    if (mode === 'timer') {
      const tick = () => { if (held) return; advance(); timer = setTimeout(tick, DWELL); };
      watch(hero, off,
        () => { if (!running) { running = true; timer = setTimeout(tick, DWELL); } },
        () => { running = false; clearTimeout(timer); });
    }
    ticks.forEach((t, j) => t.addEventListener('click', () => { held = true; clearTimeout(timer); show(j); }, { signal: off.signal }));
    if (bar) bar.hidden = false;
    show(at);

    return {
      advance,
      pick(el) { const k = slots.indexOf(el); if (k >= 0) { held = true; clearTimeout(timer); show(k); } },
      stop() { off.abort(); clearTimeout(timer); }
    };
  }

  function initBg() {
    if (bg) { bg.stop(); bg = null; }
    if (pieces) { pieces.stop(); pieces = null; }
    const hero = document.querySelector('.aj-hero');
    if (!hero) return;
    const word = (hero.dataset.word || '').replace(/\s+/g, '').toLowerCase() || 'andjulystudios';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mode = reduced ? 'still' : (hero.dataset.bg || 'type');
    const token = bgToken = {};
    hero.dataset.mode = mode;
    pieces = startPieces(hero, reduced ? 'off' : mode === 'type' ? 'type' : 'timer');
    if (mode === 'type') bg = startType(hero, word, () => { if (pieces) pieces.advance(); });
    if (mode !== 'loop') return;
    const canvas = hero.querySelector('.aj-trail');
    if (!canvas) return;
    const go = () => { if (bgToken === token) bg = startLoop(hero, canvas, word); };
    const fontReady = document.fonts && document.fonts.load ? document.fonts.load(FONT) : Promise.resolve();
    Promise.race([fontReady, new Promise((r) => setTimeout(r, 1500))]).then(go, go);
  }

  /* ---------- Images: fade in when loaded, designed fallback when missing ---------- */
  function initImages(root) {
    root.querySelectorAll('.aj-img').forEach((img) => {
      const frame = img.closest('.aj-frame');
      const done = () => { img.classList.add('is-in'); if (frame) frame.classList.add('is-in'); };
      const fail = () => { if (frame) frame.classList.add('is-missing'); };
      if (img.complete) { img.naturalWidth ? done() : fail(); return; }
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', fail, { once: true });
    });
  }

  /* ---------- Owner password dialog ---------- */
  function initDialog() {
    const dlg = document.getElementById('aj-pass');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    const open = () => {
      if (!dlg.open) dlg.showModal();
      const input = dlg.querySelector('input[type="password"]');
      if (input) input.focus();
    };
    document.querySelectorAll('[data-aj-open="aj-pass"]').forEach((b) => b.addEventListener('click', open));
    dlg.querySelectorAll('[data-aj-close]').forEach((b) => b.addEventListener('click', () => dlg.close()));
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    if (dlg.querySelector('[data-aj-autoopen]')) open();
  }

  /* ---------- Sign-up: busy state on submit, focus the confirmation ---------- */
  function initJoin() {
    const form = document.getElementById('aj-join');
    if (form) {
      form.addEventListener('submit', () => {
        const btn = form.querySelector('[data-aj-submit]');
        if (!btn) return;
        btn.setAttribute('aria-busy', 'true');
        btn.textContent = btn.dataset.busyLabel || btn.textContent;
        form.classList.add('is-busy');
      });
    }
    const done = document.querySelector('[data-aj-done]');
    if (done) {
      done.scrollIntoView({ block: 'center' });
      done.focus({ preventScroll: true });
    }
    document.querySelectorAll('[data-aj-tojoin]').forEach((a) => a.addEventListener('click', () => {
      const email = document.getElementById('aj-email');
      if (email) setTimeout(() => email.focus({ preventScroll: true }), 400);
    }));
  }

  initBg();
  initImages(document);
  initDialog();
  initJoin();

  document.addEventListener('shopify:section:load', (e) => {
    initImages(e.target);
    if (e.target.querySelector('.aj-hero')) initBg();
  });
  // Theme editor: picking a Product block shows it; leaving it lets the products turn again.
  document.addEventListener('shopify:block:select', (e) => { if (pieces) pieces.pick(e.target); });
  document.addEventListener('shopify:block:deselect', (e) => { if (e.target.classList.contains('aj-piece__slot')) initBg(); });
})();
